/**
 * BlackboardPage -- Admin/Superadmin News-Verwaltung
 * ===================================================
 * News-Style Editor mit:
 *  - Firmen-Auswahl per Overlay (Klarnamen, Suche, Multi-Select)
 *  - Rich-Text Editor mit Formatierungs-Toolbar
 *  - Layout-Auswahl (Text / Bild links / Bild rechts / Bild oben)
 *  - Live-Preview wie der Beitrag spaeter erscheint
 *  - Angepinnte Beitraege im eigenen Bereich
 */
import { useState, useEffect, useCallback, useRef, useMemo, type ReactNode } from 'react'
import DOMPurify from 'dompurify'
import type {
  BlackboardPost,
  BlackboardAttachment,
  BlackboardLayout,
  BlackboardCategory,
  BlackboardFirma,
} from '@/types'

const API = '/api/blackboard'

// ── API-Helfer ───────────────────────────────────────────────

async function request<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...opts.headers },
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || data.detail || 'Anfrage fehlgeschlagen')
  return data
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '--'
  try {
    return new Date(iso).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function fmtFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function isImage(att: BlackboardAttachment): boolean {
  return att.attachment_type === 'image'
}

function fileUrl(postId: number, filename: string): string {
  return `${API}/files/${postId}/${filename}`
}

// ── Shared UI ────────────────────────────────────────────────

const FOM_TEAL = '#009B8D'
const FOM_TEAL_DARK = '#006B5F'
const FOM_TEAL_BG = '#e6f7f5'

function Loading() {
  return <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Laden...</div>
}

function Card({ children, borderLeft, padding = 24 }: { children: ReactNode; borderLeft?: string; padding?: number }) {
  return (
    <div style={{
      background: 'white', borderRadius: 12, border: '1px solid #e5e7eb',
      borderLeft: borderLeft ? `4px solid ${borderLeft}` : undefined, overflow: 'hidden',
    }}>
      <div style={{ padding }}>{children}</div>
    </div>
  )
}

function Tag({ children, color = 'teal' }: { children: ReactNode; color?: 'teal' | 'gray' | 'orange' | 'red' | 'blue' }) {
  const colors = {
    teal: { bg: FOM_TEAL_BG, fg: FOM_TEAL_DARK },
    gray: { bg: '#f3f4f6', fg: '#6b7280' },
    orange: { bg: '#fef3c7', fg: '#b45309' },
    red: { bg: '#fee2e2', fg: '#dc2626' },
    blue: { bg: '#dbeafe', fg: '#1d4ed8' },
  }
  const c = colors[color]
  return (
    <span style={{
      background: c.bg, color: c.fg, padding: '4px 12px',
      borderRadius: 14, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  )
}

function PillBtn({ children, onClick, teal, danger, disabled }: { children: ReactNode; onClick?: () => void; teal?: boolean; danger?: boolean; disabled?: boolean }) {
  return (
    <button onClick={onClick} type="button" disabled={disabled} style={{
      padding: '8px 20px', background: 'white',
      border: `1.5px solid ${danger ? '#dc2626' : teal ? FOM_TEAL : '#e5e7eb'}`,
      borderRadius: 20, fontSize: 13, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
      color: danger ? '#dc2626' : teal ? FOM_TEAL : '#374151',
      opacity: disabled ? 0.5 : 1, transition: 'all .15s',
    }}>
      {children}
    </button>
  )
}

const btnPrimary: React.CSSProperties = {
  padding: '10px 24px', background: FOM_TEAL, color: 'white', border: 'none',
  borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e5e7eb',
  fontSize: 14, outline: 'none', boxSizing: 'border-box',
}

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151',
}

// ═══════════════════════════════════════════════════════════════
// Hauptkomponente
// ═══════════════════════════════════════════════════════════════

export default function BlackboardPage() {
  const [view, setView] = useState<'list' | 'editor'>('list')
  const [editPost, setEditPost] = useState<BlackboardPost | null>(null)
  const [tenants, setTenants] = useState<BlackboardFirma[]>([])
  const [tenantsLoading, setTenantsLoading] = useState(true)
  const [tenantsError, setTenantsError] = useState<string | null>(null)

  const loadTenants = useCallback(async () => {
    setTenantsLoading(true)
    setTenantsError(null)
    try {
      const res = await request<{ data: BlackboardFirma[] }>('/admin/firmen')
      setTenants(res.data || [])
    } catch (e: any) {
      setTenants([])
      setTenantsError(e?.message || 'Firmen konnten nicht geladen werden')
    } finally {
      setTenantsLoading(false)
    }
  }, [])

  useEffect(() => { loadTenants() }, [loadTenants])

  function openNew() { setEditPost(null); setView('editor') }
  function openEdit(post: BlackboardPost) { setEditPost(post); setView('editor') }
  function closeEditor() { setEditPost(null); setView('list') }

  return (
    <div style={{ padding: '32px 48px', maxWidth: 1280, margin: '0 auto' }}>
      {view === 'list' && <PostList onNew={openNew} onEdit={openEdit} tenants={tenants} />}
      {view === 'editor' && (
        <PostEditor
          post={editPost}
          onClose={closeEditor}
          tenants={tenants}
          tenantsLoading={tenantsLoading}
          tenantsError={tenantsError}
          onReloadTenants={loadTenants}
        />
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Post-Liste (News-Style mit Pinned-Bereich)
// ═══════════════════════════════════════════════════════════════

type StatusTab = 'published' | 'draft' | 'archived'

function PostList({ onNew, onEdit, tenants }: { onNew: () => void; onEdit: (p: BlackboardPost) => void; tenants: BlackboardFirma[] }) {
  const [tab, setTab] = useState<StatusTab>('published')
  const [posts, setPosts] = useState<BlackboardPost[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const pageSize = 20

  const tenantById = useMemo(() => {
    const map = new Map<number, string>()
    tenants.forEach(t => map.set(t.firma_id, t.name))
    return map
  }, [tenants])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await request<any>(`/admin/posts?status=${tab}&page=${page}&pageSize=${pageSize}`)
      setPosts(res.data || [])
      setTotal(res.total || 0)
    } catch {
      setPosts([])
    } finally {
      setLoading(false)
    }
  }, [tab, page])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(1) }, [tab])

  async function handleDelete(id: number) {
    if (!confirm('Beitrag wirklich loeschen?')) return
    try {
      await request(`/admin/posts/${id}`, { method: 'DELETE' })
      load()
    } catch (e: any) { alert(`Fehler: ${e.message}`) }
  }

  async function handleArchive(id: number) {
    try {
      await request(`/admin/posts/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status: 'archived' }) })
      load()
    } catch (e: any) { alert(`Fehler: ${e.message}`) }
  }

  async function handlePublish(id: number) {
    try {
      await request(`/admin/posts/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status: 'published' }) })
      load()
    } catch (e: any) { alert(`Fehler: ${e.message}`) }
  }

  async function handleTogglePin(post: BlackboardPost) {
    try {
      await request(`/admin/posts/${post.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_pinned: !post.is_pinned }),
      })
      load()
    } catch (e: any) { alert(`Fehler: ${e.message}`) }
  }

  const tabs: { id: StatusTab; label: string }[] = [
    { id: 'published', label: 'Veroeffentlicht' },
    { id: 'draft', label: 'Entwuerfe' },
    { id: 'archived', label: 'Archiviert' },
  ]

  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const pinned = posts.filter(p => p.is_pinned)
  const regular = posts.filter(p => !p.is_pinned)

  function targetLabel(post: BlackboardPost): string {
    if (post.target_mode === 'all') return 'Alle Firmen'
    const ids = post.target_firma_ids || []
    if (ids.length === 0) return 'Keine Firma'
    const names = ids.map(id => tenantById.get(id)).filter(Boolean) as string[]
    if (names.length === 0) return `${ids.length} Firma(en)`
    if (names.length <= 2) return names.join(', ')
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`
  }

  function renderRow(post: BlackboardPost) {
    return (
      <Card key={post.id} borderLeft={post.is_pinned ? FOM_TEAL : undefined}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          {/* Optional Hero-Thumb */}
          {post.attachments.find(isImage) && (
            <img
              src={fileUrl(post.id, (post.attachments.find(isImage) as BlackboardAttachment).filename)}
              alt=""
              style={{ width: 96, height: 72, objectFit: 'cover', borderRadius: 8, flexShrink: 0, border: '1px solid #e5e7eb' }}
            />
          )}

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>{post.title}</h3>
              {post.is_pinned && <Tag color="teal">Angepinnt</Tag>}
              {post.category === 'info' ? <Tag color="blue">Info</Tag> : <Tag color="gray">News</Tag>}
              {post.status === 'draft' && <Tag color="orange">Entwurf</Tag>}
              {post.status === 'archived' && <Tag color="gray">Archiviert</Tag>}
            </div>

            {/* Plain Preview (HTML strip) */}
            <p style={{
              margin: '0 0 10px', fontSize: 14, color: '#666', lineHeight: 1.5,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {stripHtml(post.content)}
            </p>

            <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#999', flexWrap: 'wrap' }}>
              <span><strong style={{ color: '#666' }}>Ziel:</strong> {targetLabel(post)}</span>
              <span><strong style={{ color: '#666' }}>Erstellt:</strong> {fmtDate(post.created_at)}</span>
              <span><strong style={{ color: '#666' }}>Autor:</strong> {post.author_name}</span>
              {post.attachments.length > 0 && <span>{post.attachments.length} Datei(en)</span>}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <PillBtn teal={post.is_pinned} onClick={() => handleTogglePin(post)}>
              {post.is_pinned ? 'Loesen' : 'Anpinnen'}
            </PillBtn>
            <PillBtn teal onClick={() => onEdit(post)}>Bearbeiten</PillBtn>
            {post.status === 'published' && <PillBtn onClick={() => handleArchive(post.id)}>Archivieren</PillBtn>}
            {post.status === 'draft' && <PillBtn teal onClick={() => handlePublish(post.id)}>Veroeffentlichen</PillBtn>}
            {post.status === 'archived' && <PillBtn teal onClick={() => handlePublish(post.id)}>Reaktivieren</PillBtn>}
            <PillBtn danger onClick={() => handleDelete(post.id)}>Loeschen</PillBtn>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.3px', margin: 0 }}>News &amp; Mitteilungen</h1>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
            Verwaltung der Beitraege fuer das Firmen-Dashboard
          </div>
        </div>
        <button onClick={onNew} style={btnPrimary}>+ Neuer Beitrag</button>
      </div>

      {/* Tabs */}
      <nav style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            color: tab === t.id ? 'white' : '#374151', padding: '8px 20px', borderRadius: 10,
            fontSize: 14, fontWeight: tab === t.id ? 700 : 500,
            background: tab === t.id ? FOM_TEAL : 'transparent',
            border: tab === t.id ? 'none' : '1px solid #e5e7eb', cursor: 'pointer', transition: 'all .15s',
          }}>
            {t.label}
          </button>
        ))}
      </nav>

      {loading ? <Loading /> : posts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
          <div style={{ fontSize: 15 }}>Keine Beitraege in dieser Kategorie.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Pinned-Bereich */}
          {pinned.length > 0 && (
            <section>
              <SectionHeader icon="pin" title="Angepinnt" subtitle="Diese Beitraege erscheinen ganz oben im Dashboard" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {pinned.map(renderRow)}
              </div>
            </section>
          )}

          {/* Regulaere Beitraege */}
          {regular.length > 0 && (
            <section>
              {pinned.length > 0 && <SectionHeader icon="news" title="Weitere Beitraege" />}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {regular.map(renderRow)}
              </div>
            </section>
          )}

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 8 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                style={{ ...pageBtnStyle, opacity: page <= 1 ? 0.4 : 1 }}>Zurueck</button>
              <span style={{ padding: '7px 16px', fontSize: 13, color: '#666' }}>Seite {page} von {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                style={{ ...pageBtnStyle, opacity: page >= totalPages ? 0.4 : 1 }}>Weiter</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function SectionHeader({ icon, title, subtitle }: { icon: 'pin' | 'news'; title: string; subtitle?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8, background: FOM_TEAL_BG,
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: FOM_TEAL_DARK,
      }}>
        {icon === 'pin' ? (
          <svg width={14} height={14} viewBox="0 0 20 20" fill="currentColor">
            <path d="M9.828 3.414a2 2 0 012.829 0l1.929 1.929a2 2 0 010 2.828l-4.243 4.243a1 1 0 01-.707.293H7.1a1 1 0 01-.707-.293L4.464 10.485a2 2 0 010-2.829L9.828 3.414z" />
          </svg>
        ) : (
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2zM12 10v4m-2-2h4" />
          </svg>
        )}
      </div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: '#9ca3af' }}>{subtitle}</div>}
      </div>
    </div>
  )
}

const pageBtnStyle: React.CSSProperties = {
  padding: '7px 16px', border: '1.5px solid #e5e7eb', borderRadius: 8,
  background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 500,
}

function stripHtml(html: string): string {
  if (!html) return ''
  // Schnelles Strip ohne DOM
  return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
}

// ═══════════════════════════════════════════════════════════════
// Post-Editor
// ═══════════════════════════════════════════════════════════════

interface EditorForm {
  title: string
  content: string
  target_mode: 'all' | 'specific'
  target_firma_ids: number[]
  status: 'published' | 'draft'
  is_pinned: boolean
  layout: BlackboardLayout
  category: BlackboardCategory
  hero_attachment_id: number | null
}

function PostEditor({
  post, onClose, tenants, tenantsLoading, tenantsError, onReloadTenants,
}: {
  post: BlackboardPost | null
  onClose: () => void
  tenants: BlackboardFirma[]
  tenantsLoading: boolean
  tenantsError: string | null
  onReloadTenants: () => void
}) {
  const isEdit = !!post
  const [form, setForm] = useState<EditorForm>({
    title: post?.title || '',
    content: post?.content || '',
    target_mode: post?.target_mode || 'all',
    target_firma_ids: post?.target_firma_ids || [],
    status: post?.status === 'archived' ? 'published' : (post?.status || 'published'),
    is_pinned: post?.is_pinned || false,
    layout: post?.layout || 'text',
    category: post?.category || 'news',
    hero_attachment_id: post?.hero_attachment_id || null,
  })
  const [attachments, setAttachments] = useState<BlackboardAttachment[]>(post?.attachments || [])
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [savedPostId, setSavedPostId] = useState<number | null>(post?.id || null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  function update<K extends keyof EditorForm>(key: K, val: EditorForm[K]) {
    setForm(f => ({ ...f, [key]: val }))
  }

  // Hero-Bild bestimmen: explizit gewaehltes oder erstes Bild
  const heroImage: BlackboardAttachment | undefined = useMemo(() => {
    if (form.hero_attachment_id) {
      return attachments.find(a => a.id === form.hero_attachment_id && isImage(a))
    }
    return attachments.find(isImage)
  }, [attachments, form.hero_attachment_id])

  // Returns the post-id on success (auch bei Neu-Anlage), null bei Fehler.
  // Idempotent: nach erstem Save schaltet PUT auf den existing savedPostId — kein neuer Eintrag bei Mehrfach-Klick.
  async function handleSave(opts: { silentStay?: boolean } = {}): Promise<number | null> {
    if (!form.title.trim()) { setError('Titel ist erforderlich.'); return null }
    if (!stripHtml(form.content).trim()) { setError('Inhalt ist erforderlich.'); return null }
    setError(null)
    setSuccess(null)
    setSaving(true)

    const body = {
      title: form.title.trim(),
      content: form.content,
      target_mode: form.target_mode,
      target_firma_ids: form.target_mode === 'specific' ? form.target_firma_ids : [],
      is_pinned: form.is_pinned,
      status: form.status,
      layout: form.layout,
      category: form.category,
      hero_attachment_id: form.hero_attachment_id,
    }

    try {
      if (savedPostId) {
        // Update — egal ob initial-Edit oder gerade selbst gespeichert (Doubletten-Schutz)
        await request(`/admin/posts/${savedPostId}`, { method: 'PUT', body: JSON.stringify(body) })
        if (opts.silentStay) {
          setSuccess('Änderungen gespeichert.')
        } else {
          onClose()
        }
        return savedPostId
      } else {
        const res = await request<any>('/admin/posts', { method: 'POST', body: JSON.stringify(body) })
        const newId = res.data?.id ?? null
        if (newId) {
          setSavedPostId(newId)
          setSuccess('Beitrag erstellt. Sie können jetzt Dateien hochladen oder den Editor schließen.')
        } else {
          onClose()
        }
        return newId
      }
    } catch (e: any) {
      setError(e.message)
      return null
    } finally {
      setSaving(false)
    }
  }

  async function handleFileUpload(files: FileList | null) {
    if (!files || files.length === 0) return
    // Auto-Save zuerst, falls noch kein Post existiert — User muss nicht erst "Speichern" drücken
    let postId = savedPostId
    if (!postId) {
      postId = await handleSave({ silentStay: true })
      if (!postId) return  // Save fehlgeschlagen — Fehlermeldung wurde von handleSave gesetzt
    }
    setUploading(true)
    setError(null)
    try {
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData()
        fd.append('file', files[i])
        const res = await fetch(`${API}/admin/posts/${postId}/upload`, {
          method: 'POST', body: fd, credentials: 'include',
        })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || 'Upload fehlgeschlagen')
        if (json.data) {
          setAttachments(prev => [...prev, json.data])
          // Erste Bild-Datei automatisch als Hero setzen
          if (isImage(json.data) && !form.hero_attachment_id) {
            update('hero_attachment_id', json.data.id)
          }
        }
      }
    } catch (e: any) {
      setError(e.message)
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  async function handleDeleteAttachment(attId: number) {
    try {
      await fetch(`${API}/admin/attachments/${attId}`, { method: 'DELETE', credentials: 'include' })
      setAttachments(prev => prev.filter(a => a.id !== attId))
      if (form.hero_attachment_id === attId) {
        update('hero_attachment_id', null)
      }
    } catch (e: any) {
      setError(e.message)
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    handleFileUpload(e.dataTransfer.files)
  }

  // Tenant-Map fuer Anzeige der gewaehlten Firmen
  const tenantById = useMemo(() => {
    const map = new Map<number, BlackboardFirma>()
    tenants.forEach(t => map.set(t.firma_id, t))
    return map
  }, [tenants])

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <button onClick={onClose} style={{
          background: 'white', border: '1px solid #e5e7eb', cursor: 'pointer',
          fontSize: 18, color: '#666', padding: '6px 12px', borderRadius: 8,
        }} title="Zurueck">&larr;</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.3px', margin: 0 }}>
            {isEdit ? 'Beitrag bearbeiten' : 'Neuer Beitrag'}
          </h1>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
            Verfasse News oder Infos fuer das Firmen-Dashboard
          </div>
        </div>
        <button onClick={() => handleSave()} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Wird gespeichert...' : savedPostId ? 'Speichern' : 'Beitrag erstellen'}
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: '#fee2e2', color: '#dc2626', borderRadius: 8, marginBottom: 20, fontSize: 14 }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ padding: '12px 16px', background: '#dcfce7', color: '#166534', borderRadius: 8, marginBottom: 20, fontSize: 14 }}>
          {success}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 28, alignItems: 'start' }}>
        {/* ── Linke Spalte: Editor + alle Settings ───────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
          <Card>
            <label style={labelStyle}>Titel</label>
            <input
              style={{ ...inputStyle, fontSize: 16, fontWeight: 600 }}
              placeholder="Aussagekraeftiger Titel"
              value={form.title}
              onChange={e => update('title', e.target.value)}
            />
          </Card>

          <Card>
            <label style={labelStyle}>Inhalt</label>
            <RichTextEditor
              initialHtml={form.content}
              onChange={html => update('content', html)}
              placeholder="Schreiben Sie Ihren Beitrag..."
            />
          </Card>

          {/* Layout-Auswahl */}
          <Card>
            <label style={labelStyle}>Darstellung</label>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 12 }}>
              Wie soll Bild und Text im Dashboard angeordnet sein?
            </div>
            <LayoutPicker value={form.layout} onChange={v => update('layout', v)} />
          </Card>

          {/* Kategorie + Pin nebeneinander */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <Card>
              <label style={labelStyle}>Kategorie</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <CategoryChip active={form.category === 'news'} onClick={() => update('category', 'news')} label="News" sub="Aktuelle Beitraege" />
                <CategoryChip active={form.category === 'info'} onClick={() => update('category', 'info')} label="Info" sub="Daueranzeigen" />
              </div>
            </Card>

            <Card>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, cursor: 'pointer' }}>
                <Toggle checked={form.is_pinned} onChange={v => update('is_pinned', v)} />
                <span style={{ fontWeight: 600 }}>Beitrag anpinnen</span>
              </label>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
                Erscheint ganz oben im Dashboard.
              </div>
            </Card>
          </div>

          {/* Status + Zielgruppe nebeneinander */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <Card>
              <label style={labelStyle}>Veroeffentlichung</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <RadioOption checked={form.status === 'published'} onChange={() => update('status', 'published')} label="Sofort veroeffentlichen" />
                <RadioOption checked={form.status === 'draft'} onChange={() => update('status', 'draft')} label="Als Entwurf speichern" />
              </div>
            </Card>

            <Card>
              <label style={labelStyle}>Zielgruppe</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <RadioOption checked={form.target_mode === 'all'} onChange={() => update('target_mode', 'all')} label="Alle Firmen" />
                <RadioOption checked={form.target_mode === 'specific'} onChange={() => update('target_mode', 'specific')} label="Bestimmte Firmen" />
              </div>

              {form.target_mode === 'specific' && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6 }}>
                    {tenantsLoading
                      ? 'Firmen werden geladen...'
                      : tenantsError
                        ? <span style={{ color: '#dc2626' }}>Fehler: {tenantsError}</span>
                        : `${tenants.length} Firma(en) verfuegbar`}
                    {tenantsError && (
                      <button type="button" onClick={onReloadTenants} style={{
                        marginLeft: 8, background: 'none', border: 'none',
                        color: FOM_TEAL_DARK, cursor: 'pointer', textDecoration: 'underline',
                        fontSize: 11, padding: 0,
                      }}>Neu laden</button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    disabled={tenantsLoading}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: 8,
                      border: `1.5px solid ${FOM_TEAL}`, background: 'white',
                      color: FOM_TEAL_DARK, fontWeight: 600, fontSize: 13,
                      cursor: tenantsLoading ? 'not-allowed' : 'pointer',
                      opacity: tenantsLoading ? 0.5 : 1,
                    }}
                  >
                    + Firmen auswaehlen
                  </button>

                  {form.target_firma_ids.length === 0 ? (
                    <div style={{ marginTop: 10, fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
                      Noch keine Firma ausgewaehlt
                    </div>
                  ) : (
                    <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {form.target_firma_ids.map(fid => {
                        const t = tenantById.get(fid)
                        return (
                          <span key={fid} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            background: FOM_TEAL_BG, color: FOM_TEAL_DARK,
                            padding: '5px 10px', borderRadius: 14, fontSize: 12, fontWeight: 600,
                          }}>
                            {t?.name || `Firma ${fid}`}
                            <button
                              type="button"
                              onClick={() => update('target_firma_ids', form.target_firma_ids.filter(x => x !== fid))}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: FOM_TEAL_DARK, padding: 0, fontSize: 14, lineHeight: 1 }}
                              title="Entfernen"
                            >&times;</button>
                          </span>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>

          {/* Dateien */}
          <Card>
            <label style={labelStyle}>Dateien &amp; Bilder</label>

            {!savedPostId && (
              <div style={{ padding: '12px 16px', background: '#eff6ff', color: '#1e40af', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
                Dateien können Sie direkt hier ablegen — der Beitrag wird automatisch zuerst gespeichert.
              </div>
            )}

            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? FOM_TEAL : '#e5e7eb'}`,
                borderRadius: 12, padding: '32px 20px', textAlign: 'center',
                cursor: savedPostId ? 'pointer' : 'not-allowed',
                background: dragOver ? '#f0faf9' : '#fafafa',
                transition: 'all .15s',
                opacity: savedPostId ? 1 : 0.5,
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 8, color: '#ccc' }}>{uploading ? '...' : '+'}</div>
              <div style={{ fontSize: 14, color: '#999' }}>
                {uploading ? 'Wird hochgeladen...' : 'Dateien hierher ziehen oder klicken'}
              </div>
            </div>
            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }}
              onChange={e => handleFileUpload(e.target.files)} />

            {attachments.length > 0 && (
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {attachments.map(att => (
                  <div key={att.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                    background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb',
                  }}>
                    {isImage(att) ? (
                      <img src={fileUrl(att.post_id, att.filename)} alt={att.original_name}
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                    ) : (
                      <div style={{
                        width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: '#e5e7eb', borderRadius: 6, fontSize: 18, color: '#999',
                      }}>D</div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.original_name}</div>
                      <div style={{ fontSize: 11, color: '#999' }}>{fmtFileSize(att.file_size)} &middot; {att.mime_type}</div>
                    </div>
                    {isImage(att) && (
                      <button
                        onClick={() => update('hero_attachment_id', att.id)}
                        type="button"
                        title="Als Beitragsbild verwenden"
                        style={{
                          padding: '6px 12px', fontSize: 12, fontWeight: 600,
                          border: `1.5px solid ${form.hero_attachment_id === att.id ? FOM_TEAL : '#e5e7eb'}`,
                          background: form.hero_attachment_id === att.id ? FOM_TEAL_BG : 'white',
                          color: form.hero_attachment_id === att.id ? FOM_TEAL_DARK : '#666',
                          borderRadius: 16, cursor: 'pointer',
                        }}
                      >
                        {form.hero_attachment_id === att.id ? 'Hero ✓' : 'Als Hero'}
                      </button>
                    )}
                    <button onClick={() => handleDeleteAttachment(att.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 18, padding: 4 }}
                      title="Datei entfernen">&times;</button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <button onClick={onClose} style={{
            alignSelf: 'flex-start',
            padding: '10px 24px', background: 'white', color: '#374151',
            border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14,
            fontWeight: 600, cursor: 'pointer',
          }}>
            Abbrechen
          </button>
        </div>

        {/* ── Rechte Spalte: Live-Preview Widget (sticky, vertikal) ── */}
        <aside style={{
          position: 'sticky', top: 24, alignSelf: 'start',
          display: 'flex', flexDirection: 'column', gap: 12,
          maxHeight: 'calc(100vh - 48px)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 4px' }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%', background: FOM_TEAL,
            }} />
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Live-Vorschau</div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>Dashboard-Widget</div>
          </div>

          <div style={{
            background: '#f3f4f6', borderRadius: 16, padding: 16,
            border: '1px solid #e5e7eb', overflow: 'auto',
            maxHeight: 'calc(100vh - 100px)',
          }}>
            <NewsPreview
              title={form.title || '(Ohne Titel)'}
              contentHtml={form.content || '<em style="color:#999">(Kein Inhalt)</em>'}
              layout={form.layout}
              category={form.category}
              isPinned={form.is_pinned}
              heroImage={heroImage}
              authorName={post?.author_name || 'Du'}
              createdAt={post?.created_at || new Date().toISOString()}
            />
          </div>
        </aside>
      </div>

      {/* Firma-Picker Overlay */}
      {pickerOpen && (
        <FirmaPickerOverlay
          tenants={tenants}
          loading={tenantsLoading}
          error={tenantsError}
          onReload={onReloadTenants}
          selected={form.target_firma_ids}
          onCancel={() => setPickerOpen(false)}
          onConfirm={ids => {
            update('target_firma_ids', ids)
            setPickerOpen(false)
          }}
        />
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Rich-Text Editor
// ═══════════════════════════════════════════════════════════════

function RichTextEditor({ initialHtml, onChange, placeholder }: { initialHtml: string; onChange: (html: string) => void; placeholder?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const [, force] = useState(0)

  // Inhalt nur einmal beim Mount setzen, sonst springt der Cursor
  useEffect(() => {
    if (ref.current && ref.current.innerHTML === '') {
      ref.current.innerHTML = initialHtml || ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function exec(cmd: string, value?: string) {
    document.execCommand(cmd, false, value)
    if (ref.current) {
      onChange(ref.current.innerHTML)
      force(n => n + 1)
    }
    ref.current?.focus()
  }

  function handleLink() {
    const url = prompt('Link-URL eingeben:', 'https://')
    if (!url) return
    exec('createLink', url)
  }

  function handleHeading(level: '1' | '2' | '3' | 'p') {
    if (level === 'p') exec('formatBlock', 'p')
    else exec('formatBlock', `h${level}`)
  }

  const btn: React.CSSProperties = {
    padding: '6px 10px', minWidth: 32, height: 32, background: 'white',
    border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer',
    fontSize: 13, fontWeight: 600, color: '#374151',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  }

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 4, padding: 8,
        background: '#fafafa', borderBottom: '1px solid #e5e7eb',
      }}>
        <button type="button" style={btn} onClick={() => handleHeading('1')} title="Ueberschrift 1">H1</button>
        <button type="button" style={btn} onClick={() => handleHeading('2')} title="Ueberschrift 2">H2</button>
        <button type="button" style={btn} onClick={() => handleHeading('3')} title="Ueberschrift 3">H3</button>
        <button type="button" style={btn} onClick={() => handleHeading('p')} title="Absatz">P</button>
        <ToolbarSep />
        <button type="button" style={{ ...btn, fontWeight: 800 }} onClick={() => exec('bold')} title="Fett (Ctrl+B)">B</button>
        <button type="button" style={{ ...btn, fontStyle: 'italic' }} onClick={() => exec('italic')} title="Kursiv (Ctrl+I)">I</button>
        <button type="button" style={{ ...btn, textDecoration: 'underline' }} onClick={() => exec('underline')} title="Unterstrichen">U</button>
        <button type="button" style={{ ...btn, textDecoration: 'line-through' }} onClick={() => exec('strikeThrough')} title="Durchgestrichen">S</button>
        <ToolbarSep />
        <button type="button" style={btn} onClick={() => exec('insertUnorderedList')} title="Aufzaehlung">•</button>
        <button type="button" style={btn} onClick={() => exec('insertOrderedList')} title="Nummerierte Liste">1.</button>
        <ToolbarSep />
        <button type="button" style={btn} onClick={() => exec('justifyLeft')} title="Linksbuendig">⯇</button>
        <button type="button" style={btn} onClick={() => exec('justifyCenter')} title="Zentriert">≡</button>
        <button type="button" style={btn} onClick={() => exec('justifyRight')} title="Rechtsbuendig">⯈</button>
        <ToolbarSep />
        <button type="button" style={btn} onClick={handleLink} title="Link einfuegen">🔗</button>
        <button type="button" style={btn} onClick={() => exec('removeFormat')} title="Formatierung loeschen">✕</button>
      </div>

      {/* Editable */}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={() => {
          if (ref.current) onChange(ref.current.innerHTML)
        }}
        style={{
          minHeight: 220, padding: 16, fontSize: 14, lineHeight: 1.6,
          outline: 'none', color: '#111827',
        }}
      />

      <style>{`
        [contenteditable][data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable] h1 { font-size: 22px; font-weight: 700; margin: 12px 0 8px; }
        [contenteditable] h2 { font-size: 18px; font-weight: 700; margin: 10px 0 6px; }
        [contenteditable] h3 { font-size: 16px; font-weight: 700; margin: 8px 0 6px; }
        [contenteditable] p  { margin: 6px 0; }
        [contenteditable] ul, [contenteditable] ol { margin: 6px 0 6px 22px; }
        [contenteditable] a  { color: ${FOM_TEAL_DARK}; text-decoration: underline; }
      `}</style>
    </div>
  )
}

function ToolbarSep() {
  return <div style={{ width: 1, background: '#e5e7eb', margin: '4px 4px' }} />
}

// ═══════════════════════════════════════════════════════════════
// Layout-Picker
// ═══════════════════════════════════════════════════════════════

function LayoutPicker({ value, onChange }: { value: BlackboardLayout; onChange: (v: BlackboardLayout) => void }) {
  const opts: { id: BlackboardLayout; label: string; render: () => ReactNode }[] = [
    { id: 'text', label: 'Nur Text', render: () => <LayoutThumb mode="text" /> },
    { id: 'image-left', label: 'Bild links', render: () => <LayoutThumb mode="image-left" /> },
    { id: 'image-right', label: 'Bild rechts', render: () => <LayoutThumb mode="image-right" /> },
    { id: 'image-top', label: 'Bild oben', render: () => <LayoutThumb mode="image-top" /> },
  ]

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
      {opts.map(o => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          style={{
            padding: 10, background: 'white',
            border: `2px solid ${value === o.id ? FOM_TEAL : '#e5e7eb'}`,
            borderRadius: 10, cursor: 'pointer', textAlign: 'center',
            transition: 'all .15s',
          }}
        >
          {o.render()}
          <div style={{
            fontSize: 11, fontWeight: value === o.id ? 700 : 500,
            color: value === o.id ? FOM_TEAL_DARK : '#6b7280', marginTop: 6,
          }}>
            {o.label}
          </div>
        </button>
      ))}
    </div>
  )
}

function LayoutThumb({ mode }: { mode: BlackboardLayout }) {
  const box: React.CSSProperties = { background: '#dbeafe', borderRadius: 3 }
  const line: React.CSSProperties = { background: '#e5e7eb', borderRadius: 2, height: 4 }

  if (mode === 'text') {
    return (
      <div style={{ height: 56, padding: 6, background: '#f9fafb', borderRadius: 6, display: 'flex', flexDirection: 'column', gap: 4, justifyContent: 'center' }}>
        <div style={{ ...line, width: '70%' }} />
        <div style={{ ...line, width: '95%' }} />
        <div style={{ ...line, width: '85%' }} />
      </div>
    )
  }
  if (mode === 'image-left') {
    return (
      <div style={{ height: 56, padding: 6, background: '#f9fafb', borderRadius: 6, display: 'flex', gap: 5, alignItems: 'center' }}>
        <div style={{ ...box, width: 22, height: '100%' }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ ...line, width: '90%' }} />
          <div style={{ ...line, width: '70%' }} />
          <div style={{ ...line, width: '85%' }} />
        </div>
      </div>
    )
  }
  if (mode === 'image-right') {
    return (
      <div style={{ height: 56, padding: 6, background: '#f9fafb', borderRadius: 6, display: 'flex', gap: 5, alignItems: 'center' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ ...line, width: '90%' }} />
          <div style={{ ...line, width: '70%' }} />
          <div style={{ ...line, width: '85%' }} />
        </div>
        <div style={{ ...box, width: 22, height: '100%' }} />
      </div>
    )
  }
  // image-top
  return (
    <div style={{ height: 56, padding: 6, background: '#f9fafb', borderRadius: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ ...box, width: '100%', height: 18 }} />
      <div style={{ ...line, width: '90%' }} />
      <div style={{ ...line, width: '70%' }} />
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// News-Preview
// ═══════════════════════════════════════════════════════════════

function NewsPreview({
  title, contentHtml, layout, category, isPinned, heroImage, authorName, createdAt,
}: {
  title: string
  contentHtml: string
  layout: BlackboardLayout
  category: BlackboardCategory
  isPinned: boolean
  heroImage?: BlackboardAttachment
  authorName: string
  createdAt: string
}) {
  const heroSrc = heroImage ? fileUrl(heroImage.post_id, heroImage.filename) : null

  const headerLine = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
      {category === 'info' ? <Tag color="blue">Info</Tag> : <Tag color="teal">News</Tag>}
      {isPinned && <Tag color="orange">Angepinnt</Tag>}
      <span style={{ fontSize: 11, color: '#9ca3af' }}>
        {fmtDate(createdAt)} &middot; {authorName}
      </span>
    </div>
  )

  const titleEl = (
    <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
      {title}
    </h2>
  )

  const contentEl = (
    <div className="newsbody" style={{ fontSize: 14, color: '#4b5563', lineHeight: 1.6 }}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contentHtml) }} />
  )

  const heroEl = heroSrc ? (
    <img src={heroSrc} alt=""
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
  ) : (
    <div style={{
      width: '100%', height: '100%', background: '#f3f4f6',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#9ca3af', fontSize: 12,
    }}>
      Kein Bild
    </div>
  )

  // Layouts
  if (layout === 'image-top') {
    return (
      <PreviewCard pinned={isPinned}>
        <div style={{ width: '100%', aspectRatio: '16/7', overflow: 'hidden', borderRadius: '12px 12px 0 0' }}>
          {heroEl}
        </div>
        <div style={{ padding: 24 }}>
          {headerLine}
          {titleEl}
          {contentEl}
        </div>
      </PreviewCard>
    )
  }

  if (layout === 'image-left' || layout === 'image-right') {
    const imgBlock = (
      <div style={{ flex: '0 0 38%', minWidth: 0, alignSelf: 'stretch' }}>
        <div style={{ width: '100%', height: '100%', minHeight: 180, overflow: 'hidden' }}>
          {heroEl}
        </div>
      </div>
    )
    const textBlock = (
      <div style={{ flex: 1, padding: 24, minWidth: 0 }}>
        {headerLine}
        {titleEl}
        {contentEl}
      </div>
    )
    return (
      <PreviewCard pinned={isPinned}>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          {layout === 'image-left' ? <>{imgBlock}{textBlock}</> : <>{textBlock}{imgBlock}</>}
        </div>
      </PreviewCard>
    )
  }

  // text only
  return (
    <PreviewCard pinned={isPinned}>
      <div style={{ padding: 24 }}>
        {headerLine}
        {titleEl}
        {contentEl}
      </div>
    </PreviewCard>
  )
}

function PreviewCard({ children, pinned }: { children: ReactNode; pinned: boolean }) {
  return (
    <div style={{
      background: 'white', borderRadius: 12, overflow: 'hidden',
      border: '1px solid #e5e7eb',
      boxShadow: pinned ? `0 0 0 2px ${FOM_TEAL_BG}` : '0 1px 2px rgba(0,0,0,0.04)',
    }}>
      {children}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Firma-Picker Overlay
// ═══════════════════════════════════════════════════════════════

function FirmaPickerOverlay({
  tenants, loading, error, onReload, selected, onCancel, onConfirm,
}: {
  tenants: BlackboardFirma[]
  loading: boolean
  error: string | null
  onReload: () => void
  selected: number[]
  onCancel: () => void
  onConfirm: (ids: number[]) => void
}) {
  const [search, setSearch] = useState('')
  const [chosen, setChosen] = useState<Set<number>>(new Set(selected))

  // Performance: bei ~48k Firmen wuerde "alle rendern" das Modal blocken.
  // Strategie:
  //  - Bereits ausgewaehlte Firmen IMMER oben mitzeigen (max 50, sonst Counter)
  //  - Daneben: erste 200 Treffer (sortiert alphabetisch, kommt aus dem Backend so)
  //  - Bei mehr Treffern Hinweis "X weitere — bitte praeziser suchen"
  const VISIBLE_LIMIT = 200

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return tenants
    return tenants.filter(t =>
      t.name.toLowerCase().includes(q) ||
      String(t.firma_id).includes(q)
    )
  }, [tenants, search])

  const filteredCount = filtered.length
  const visible = useMemo(() => filtered.slice(0, VISIBLE_LIMIT), [filtered])
  const hiddenCount = Math.max(0, filteredCount - VISIBLE_LIMIT)

  function toggle(id: number) {
    setChosen(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function selectAllVisible() {
    setChosen(prev => {
      const next = new Set(prev)
      visible.forEach(t => next.add(t.firma_id))
      return next
    })
  }

  function clearAll() {
    setChosen(new Set())
  }

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, backdropFilter: 'blur(2px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 16, width: '100%', maxWidth: 720,
          maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>Firmen auswaehlen</div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                {loading
                  ? 'Laden...'
                  : `${chosen.size} von ${tenants.length} Firmen ausgewaehlt`}
              </div>
            </div>
            <button onClick={onCancel} style={{
              background: 'none', border: 'none', fontSize: 22, color: '#9ca3af',
              cursor: 'pointer', padding: 4, lineHeight: 1,
            }}>&times;</button>
          </div>

          <input
            autoFocus
            placeholder="Firma suchen..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              ...inputStyle, padding: '12px 16px', fontSize: 14,
              background: '#f9fafb', border: '1px solid #e5e7eb',
            }}
          />

          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" onClick={selectAllVisible} style={miniBtn}>
              Sichtbare auswaehlen
            </button>
            <button type="button" onClick={clearAll} style={miniBtn}>
              Auswahl leeren
            </button>
          </div>
        </div>

        {/* Liste */}
        <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
              Firmen werden geladen...
            </div>
          ) : error ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ color: '#dc2626', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                Firmen konnten nicht geladen werden
              </div>
              <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 16 }}>{error}</div>
              <button type="button" onClick={onReload} style={{
                padding: '8px 18px', background: FOM_TEAL, color: 'white',
                border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>
                Erneut versuchen
              </button>
            </div>
          ) : tenants.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ color: '#374151', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                Keine Firmen verfuegbar
              </div>
              <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 16 }}>
                Es sind aktuell keine Firmen mit Portal-Zugang in der Datenbank.
              </div>
              <button type="button" onClick={onReload} style={{
                padding: '8px 18px', background: 'white', color: FOM_TEAL_DARK,
                border: `1.5px solid ${FOM_TEAL}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>
                Neu laden
              </button>
            </div>
          ) : filteredCount === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
              Keine Firma gefunden
            </div>
          ) : visible.map(t => {
            const isOn = chosen.has(t.firma_id)
            return (
              <button
                key={t.firma_id}
                type="button"
                onClick={() => toggle(t.firma_id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  width: '100%', padding: '12px 24px', background: isOn ? FOM_TEAL_BG : 'white',
                  border: 'none', borderBottom: '1px solid #f3f4f6',
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div style={{
                  width: 22, height: 22, borderRadius: 6,
                  border: `2px solid ${isOn ? FOM_TEAL : '#d1d5db'}`,
                  background: isOn ? FOM_TEAL : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {isOn && (
                    <svg width={12} height={12} viewBox="0 0 16 16" fill="none">
                      <path d="M3 8l3 3 7-7" stroke="white" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.name}
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                    Firma-ID {t.firma_id}
                  </div>
                </div>
              </button>
            )
          })}
          {hiddenCount > 0 && (
            <div style={{
              padding: '14px 24px', textAlign: 'center', color: '#6b7280',
              fontSize: 12, background: '#f9fafb', borderTop: '1px solid #f3f4f6',
            }}>
              <strong>{hiddenCount}</strong> weitere Treffer — bitte praeziser suchen
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
        }}>
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            <strong style={{ color: '#111827' }}>{chosen.size}</strong> Firma(en) ausgewaehlt
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onCancel} style={{
              padding: '10px 20px', background: 'white', color: '#374151',
              border: '1.5px solid #e5e7eb', borderRadius: 10,
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}>
              Abbrechen
            </button>
            <button type="button" onClick={() => onConfirm(Array.from(chosen))} style={btnPrimary}>
              Uebernehmen
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const miniBtn: React.CSSProperties = {
  padding: '6px 12px', background: 'white', border: '1px solid #e5e7eb',
  borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer',
}

// ═══════════════════════════════════════════════════════════════
// Kleine UI-Helfer
// ═══════════════════════════════════════════════════════════════

function CategoryChip({ active, onClick, label, sub }: { active: boolean; onClick: () => void; label: string; sub: string }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: 12, background: active ? FOM_TEAL_BG : 'white',
      border: `2px solid ${active ? FOM_TEAL : '#e5e7eb'}`,
      borderRadius: 10, cursor: 'pointer', textAlign: 'center', transition: 'all .15s',
    }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: active ? FOM_TEAL_DARK : '#374151' }}>{label}</div>
      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{sub}</div>
    </button>
  )
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div onClick={() => onChange(!checked)} style={{
      width: 44, height: 24, borderRadius: 12, position: 'relative', cursor: 'pointer',
      background: checked ? FOM_TEAL : '#e5e7eb', transition: 'background .2s', flexShrink: 0,
    }}>
      <div style={{
        width: 18, height: 18, borderRadius: '50%', background: 'white',
        position: 'absolute', top: 3, left: checked ? 23 : 3,
        transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  )
}

function RadioOption({ checked, onChange, label }: { checked: boolean; onChange: () => void; label: string }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, cursor: 'pointer' }}>
      <input type="radio" checked={checked} onChange={onChange} style={{ accentColor: FOM_TEAL }} />
      {label}
    </label>
  )
}
